// View Quotes → "Duplicates" drawer: every likely-duplicate group, searchable,
// each expandable into the side-by-side compare + archive view.
import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Copy, Loader2, Search, X } from "lucide-react";
import api from "../../../lib/api";
import DuplicateCompare from "./DuplicateCompare";

export default function DuplicateQuotesDrawer({ onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/quote-duplicates").then(({ data }) => setD(data)).catch(() => setD({ groups: [] })), []);
  useEffect(() => { load(); }, [load]);
  const groups = useMemo(() => {
    const n = q.trim().toLowerCase();
    const gs = d?.groups || [];
    return n ? gs.filter((g) => [g.email, ...g.quotes.flatMap((x) => [x.quote_ref, x.customer, x.am])].some((v) => String(v || "").toLowerCase().includes(n))) : gs;
  }, [d, q]);

  return (
    <div className="fixed inset-0 z-[130] flex justify-end" data-testid="dupes-drawer">
      <div className="absolute inset-0 bg-slate-950/60" onClick={onClose} />
      <div className="relative w-full max-w-[980px] h-full bg-[#0F1420] border-l border-white/10 flex flex-col">
        <div className="px-5 py-4 border-b border-white/10 flex items-center gap-2">
          <Copy className="w-5 h-5 text-amber-300" />
          <div>
            <div className="text-[15px] font-semibold text-white">Duplicate quotes</div>
            <div className="text-[11.5px] text-slate-400">Same customer, same items &amp; quantities (or same products within 2%), created within 30 days.</div>
          </div>
          <button type="button" onClick={onClose} className="ml-auto p-1.5 rounded hover:bg-white/10 text-slate-400" aria-label="Close" data-testid="dupes-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-3 border-b border-white/[0.06] flex items-center gap-3">
          <div className="relative flex-1"><Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ref, customer, email, AM…" data-testid="dupes-search"
              className="w-full h-9 pl-8 pr-2 rounded-lg bg-slate-900/70 border border-white/10 text-[12.5px] text-white outline-none focus:border-amber-400/60" /></div>
          <span className="text-[11px] font-mono text-slate-400" data-testid="dupes-count">{d ? `${groups.length} group${groups.length === 1 ? "" : "s"} · ${groups.reduce((s, g) => s + g.quotes.length, 0)} quotes` : ""}</span>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
          {!d && <div className="py-10 text-center text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Checking for duplicates…</div>}
          {d && groups.length === 0 && <div className="py-10 text-center text-slate-500 text-sm" data-testid="dupes-empty">No duplicate quotes found.</div>}
          {groups.map((g) => {
            const key = g.quotes.map((x) => x.id).join("|");
            const isOpen = open === key;
            return (
              <div key={key} className="rounded-lg border border-white/10 bg-white/[0.02]" data-testid={`dupes-group-${g.quotes[0].quote_ref}`}>
                <button type="button" onClick={() => setOpen(isOpen ? null : key)} className="w-full px-3 py-2.5 flex items-center gap-3 text-left hover:bg-white/[0.03]">
                  {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-white truncate">{g.quotes[0].customer}</div>
                    <div className="text-[11px] text-slate-400 font-mono truncate">{g.quotes.map((x) => x.quote_ref).join(" · ")}</div>
                  </div>
                  <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border border-amber-500/30 text-amber-200 shrink-0">{g.reason}</span>
                  <span className="text-[11px] font-mono text-slate-300 shrink-0">{g.quotes.length} quotes</span>
                </button>
                {isOpen && (
                  <div className="px-3 pb-3">
                    <DuplicateCompare quotes={g.quotes} onArchived={() => { setOpen(null); load(); onChanged?.(); }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
